/**
 * MailVotes postcard-mailer API client. Thin wrappers over
 * /api/mailvotes/postcards/* — auth token attached by authFetch.
 */
import { authFetch } from './authFetch';

async function json(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || data.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data;
}

export const postcardService = {
  async quote(quantity, designType = 'ai') {
    const res = await authFetch(`/api/mailvotes/postcards/pricing/quote?quantity=${encodeURIComponent(quantity)}&designType=${encodeURIComponent(designType)}`);
    return (await json(res)).data;
  },

  async uploadHeadshot(orderId, imageB64) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/headshot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageB64 }),
    });
    return (await json(res)).data;
  },

  async createOrder(payload) {
    const res = await authFetch('/api/mailvotes/postcards/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return (await json(res)).data; // { orderId, setupIntentClientSecret, quote }
  },

  async updateOrder(orderId, payload) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return (await json(res)).data;
  },

  async confirmCard(orderId) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/confirm-card`, { method: 'POST' });
    return (await json(res)).data;
  },

  // A card-save client secret for an existing order (after a declined charge,
  // or when the order was reopened after a reload).
  async newSetupIntent(orderId) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/setup-intent`, { method: 'POST' });
    return (await json(res)).data; // { setupIntentClientSecret }
  },

  // Exact size of the mail list (the voter search page's total can be an estimate).
  async listPreview(searchParameters) {
    const res = await authFetch('/api/mailvotes/postcards/list-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ searchParameters }),
    });
    return (await json(res)).data; // { voterCount, maxQuantity }
  },

  // Editor "Generate image": one AI image from a free-text prompt (base64).
  async generateImage(prompt, size) {
    const res = await authFetch('/api/mailvotes/postcards/generate-image', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, size }),
    });
    return (await json(res)).data; // { imageB64, model }
  },

  async generateDesign(orderId) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/generate-design`, { method: 'POST' });
    return (await json(res)).data;
  },

  // Design-first: generate previews BEFORE an order exists (drafts per user).
  async previewDesigns(payload) {
    const res = await authFetch('/api/mailvotes/postcards/designs/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return (await json(res)).data; // { count, previews, arts, rawArts, overlays, designs, mode }
  },

  // Multi-variant generation: one call → several front designs (shared back).
  /** Iterate on card art already generated, instead of regenerating from scratch. */
  async refineArt(artB64, instruction) {
    const res = await authFetch('/api/mailvotes/postcards/refine-art', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ artB64, instruction }),
    });
    return (await json(res)).data; // { artB64, geometry }
  },

  async generateVariants(orderId, designPrompt, mode) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/generate-variants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ designPrompt: designPrompt || undefined, mode: mode || undefined }),
    });
    return (await json(res)).data; // { count, previews, rawArts, overlays, designs, mode }
  },

  async chooseVariant(orderId, index) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/choose-variant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ index }),
    });
    return (await json(res)).data;
  },

  // Persist a Postcard Studio design — the server renders it as the proof and
  // keeps a ~300 DPI copy for the printer handoff.
  async saveDesign(orderId, design) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/design`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ design }),
    });
    return (await json(res)).data; // { status, rendered }
  },

  async getProof(orderId) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/proof`);
    return (await json(res)).data; // { front, back, artFront, artBack, frontDesign }
  },

  async requestRevision(orderId, notes) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/request-revision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    });
    return (await json(res)).data;
  },

  async approve(orderId) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/approve`, { method: 'POST' });
    return (await json(res)).data;
  },

  async sendToPrinter(orderId) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}/send-to-printer`, { method: 'POST' });
    return (await json(res)).data;
  },

  async listOrders() {
    const res = await authFetch('/api/mailvotes/postcards/orders');
    return (await json(res)).data;
  },

  async getOrder(orderId) {
    const res = await authFetch(`/api/mailvotes/postcards/orders/${orderId}`);
    return (await json(res)).data;
  },
};

export default postcardService;
